import type { AeEvaluatedSection, AeTemplateLogicRule } from '@/types/assessmentEngine';

export type AeLogicJumpTrigger = 'question' | 'subsection' | 'section';

export interface NavigableQuestion {
  id: string;
  question_key: string;
  section_id: string;
}

export interface PlacedQuestion extends NavigableQuestion {
  top_section_id: string;
  container_section_id: string;
}

export function getRuleTriggerScope(rule: AeTemplateLogicRule): AeLogicJumpTrigger {
  const scope = rule.trigger_scope;
  if (scope === 'subsection' || scope === 'section') return scope;
  return 'question';
}

function ruleMatchesAnswer(rule: AeTemplateLogicRule, answer: string | null): boolean {
  if (answer === null) return false;
  const conditions = (rule.condition_values as unknown[]) ?? [];
  const norm = (s: string) => s.toUpperCase();
  if (rule.operator === 'equals') {
    return conditions.some((c) => norm(String(c)) === norm(answer));
  }
  if (rule.operator === 'not_equals') {
    return !conditions.some((c) => norm(String(c)) === norm(answer));
  }
  return false;
}

function findFirstInSection(
  sections: AeEvaluatedSection[],
  sectionId: string
): NavigableQuestion | null {
  for (const sec of sections) {
    if (sec.id === sectionId && sec.visible) {
      const q = sec.questions.find((x) => x.visible);
      if (q) return { id: q.id, question_key: q.question_key, section_id: sec.id };
    }
    const sub = findFirstInSection(sec.subsections, sectionId);
    if (sub) return sub;
  }
  return null;
}

function findQuestionKeyById(sections: AeEvaluatedSection[], questionId: string): string | null {
  for (const sec of sections) {
    const q = sec.questions.find((x) => x.id === questionId);
    if (q) return q.question_key;
    const sub = findQuestionKeyById(sec.subsections, questionId);
    if (sub) return sub;
  }
  return null;
}

/** Walk evaluated tree; collect visible questions with subsection/section placement. */
export function flattenPlacedQuestions(sections: AeEvaluatedSection[]): PlacedQuestion[] {
  const out: PlacedQuestion[] = [];
  const walk = (list: AeEvaluatedSection[], topSectionId: string) => {
    for (const sec of list) {
      const topId = topSectionId || sec.id;
      for (const q of sec.questions) {
        if (q.visible) {
          out.push({
            id: q.id,
            question_key: q.question_key,
            section_id: sec.id,
            top_section_id: topId,
            container_section_id: sec.id,
          });
        }
      }
      walk(sec.subsections, topId);
    }
  };
  walk(sections, '');
  return out;
}

function isLastInGroup(flat: PlacedQuestion[], currentKey: string, field: 'container_section_id' | 'top_section_id'): boolean {
  const current = flat.find((q) => q.question_key === currentKey);
  if (!current) return false;
  const group = flat.filter((q) => q[field] === current[field]);
  return group[group.length - 1]?.question_key === currentKey;
}

function trySkipJump(
  rules: AeTemplateLogicRule[],
  scope: AeLogicJumpTrigger,
  sections: AeEvaluatedSection[],
  answers: Record<string, string | number | boolean | null>,
  opts: {
    sourceQuestionId?: string;
    boundaryId?: string;
    boundaryField?: 'container_section_id' | 'top_section_id';
    flat: PlacedQuestion[];
  }
): string | null {
  const candidates = rules.filter(
    (r) => r.action === 'skip' && r.target_section_id && getRuleTriggerScope(r) === scope
  );

  for (const rule of candidates) {
    if (scope === 'question') {
      if (rule.source_question_id !== opts.sourceQuestionId) continue;
    } else {
      const source = opts.flat.find((q) => q.id === rule.source_question_id);
      if (!source || !opts.boundaryId || source[opts.boundaryField!] !== opts.boundaryId) continue;
    }

    const sourceKey = findQuestionKeyById(sections, rule.source_question_id);
    if (!sourceKey) continue;
    const raw = answers[sourceKey];
    const answer = raw === undefined || raw === null ? null : String(raw);
    if (!ruleMatchesAnswer(rule, answer)) continue;

    const target = findFirstInSection(sections, rule.target_section_id!);
    if (target) return target.question_key;
  }
  return null;
}

/** Next question after currentKey respecting skip rules and trigger scope. */
export function getNextQuestionKeyWithTriggers(
  currentKey: string,
  sections: AeEvaluatedSection[],
  rules: AeTemplateLogicRule[],
  answers: Record<string, string | number | boolean | null>
): string | null {
  const flat = flattenPlacedQuestions(sections);
  const idx = flat.findIndex((q) => q.question_key === currentKey);
  if (idx < 0) return null;

  const current = flat[idx]!;

  const questionJump = trySkipJump(rules, 'question', sections, answers, {
    sourceQuestionId: current.id,
    flat,
  });
  if (questionJump && questionJump !== currentKey) return questionJump;

  if (isLastInGroup(flat, currentKey, 'container_section_id')) {
    const subsectionJump = trySkipJump(rules, 'subsection', sections, answers, {
      boundaryId: current.container_section_id,
      boundaryField: 'container_section_id',
      flat,
    });
    if (subsectionJump && subsectionJump !== currentKey) return subsectionJump;
  }

  if (isLastInGroup(flat, currentKey, 'top_section_id')) {
    const sectionJump = trySkipJump(rules, 'section', sections, answers, {
      boundaryId: current.top_section_id,
      boundaryField: 'top_section_id',
      flat,
    });
    if (sectionJump && sectionJump !== currentKey) return sectionJump;
  }

  return flat[idx + 1]?.question_key ?? null;
}

function collectSubsectionIds(sections: AeEvaluatedSection[], topSectionId: string): string[] {
  const ids: string[] = [];
  const top = sections.find((s) => s.id === topSectionId);
  if (!top) return ids;
  const walk = (list: AeEvaluatedSection[]) => {
    for (const sec of list) {
      if (!sec.visible) continue;
      ids.push(sec.id);
      walk(sec.subsections);
    }
  };
  walk(top.subsections);
  return ids;
}

/** After completing a presentation step, honor scoped skip rules. */
export function nextPresentationStepIndexWithTriggers(
  completedStep: { id: string; questions: Array<{ id: string; question_key: string }> },
  currentStepIndex: number,
  steps: Array<{ questions: Array<{ question_key: string }> }>,
  sections: AeEvaluatedSection[],
  rules: AeTemplateLogicRule[],
  answers: Record<string, string | number | boolean | null>,
  presentationStyle: 'section' | 'subsection'
): number {
  const flat = flattenPlacedQuestions(sections);
  const stepIndexForKey = (jumpKey: string) =>
    steps.findIndex((s) => s.questions.some((x) => x.question_key === jumpKey));

  for (let i = completedStep.questions.length - 1; i >= 0; i--) {
    const q = completedStep.questions[i]!;
    const jumpKey = trySkipJump(rules, 'question', sections, answers, {
      sourceQuestionId: q.id,
      flat,
    });
    if (jumpKey) {
      const targetIdx = stepIndexForKey(jumpKey);
      if (targetIdx >= 0) return targetIdx;
    }
  }

  if (presentationStyle === 'subsection') {
    const jumpKey = trySkipJump(rules, 'subsection', sections, answers, {
      boundaryId: completedStep.id,
      boundaryField: 'container_section_id',
      flat,
    });
    if (jumpKey) {
      const targetIdx = stepIndexForKey(jumpKey);
      if (targetIdx >= 0) return targetIdx;
    }
  }

  if (presentationStyle === 'section') {
    for (const subId of collectSubsectionIds(sections, completedStep.id)) {
      const jumpKey = trySkipJump(rules, 'subsection', sections, answers, {
        boundaryId: subId,
        boundaryField: 'container_section_id',
        flat,
      });
      if (jumpKey) {
        const targetIdx = stepIndexForKey(jumpKey);
        if (targetIdx >= 0) return targetIdx;
      }
    }

    const jumpKey = trySkipJump(rules, 'section', sections, answers, {
      boundaryId: completedStep.id,
      boundaryField: 'top_section_id',
      flat,
    });
    if (jumpKey) {
      const targetIdx = stepIndexForKey(jumpKey);
      if (targetIdx >= 0) return targetIdx;
    }
  }

  return currentStepIndex + 1;
}
